import React, { useEffect, useMemo, useState } from "react";
import {
  getPedidosDisponiblesOrdenPedido,
  generarOrdenPedido,
} from "../helpers/rutaOrdenPedido.js";
import "../css/organizarpedido.css";

const normalizar = (v = "") =>
  String(v || "")
    .trim()
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const texto = (v, fallback = "-") => {
  const t = String(v ?? "").trim();
  return t || fallback;
};

const fechaCorta = (fecha) => {
  if (!fecha) return "-";
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("es-AR");
};

const getTipoPedido = (item) => {
  return (
    item?.grupoRecepcion ||
    item?.tipoMotor ||
    item?.categoriaNombre ||
    "SIN TIPO"
  );
};

const getGrupoValor = (item, agruparPor) => {
  switch (agruparPor) {
    case "PRODUCTO":
      return item.articulo || "SIN PRODUCTO";
    case "PROVINCIA":
      return item.provincia || "SIN PROVINCIA";
    case "MUNICIPIO":
      return item.municipio || "SIN MUNICIPIO";
    case "ZONA":
      return item.zonaLogistica || "SIN ZONA";
    case "TIPO":
      return getTipoPedido(item);
    default:
      return "";
  }
};

const ordenarLocal = (items, ordenarPor) => {
  const copia = [...items];

  copia.sort((a, b) => {
    const getValor = (item) => {
      switch (ordenarPor) {
        case "PROVINCIA":
          return item.provincia;
        case "MUNICIPIO":
          return item.municipio;
        case "ZONA":
          return item.zonaLogistica;
        case "FECHA":
          return item.fechaPedidoCliente;
        case "RECEPTOR":
          return item.nombreReceptor;
        case "TRACK":
          return item.track;
        case "TIPO":
          return getTipoPedido(item);
        case "PRODUCTO":
        default:
          return item.articulo;
      }
    };

    if (ordenarPor === "FECHA") {
      return (
        new Date(a.fechaPedidoCliente || 0) -
        new Date(b.fechaPedidoCliente || 0)
      );
    }

    const va = normalizar(getValor(a));
    const vb = normalizar(getValor(b));

    if (va === vb) {
      return normalizar(a.nombreReceptor).localeCompare(
        normalizar(b.nombreReceptor)
      );
    }

    return va.localeCompare(vb);
  });

  return copia;
};

export default function OrganizarPedido() {
  const [pedidos, setPedidos] = useState([]);
  const [resumen, setResumen] = useState({});
  const [seleccionados, setSeleccionados] = useState(new Set());
  const [itemsOrden, setItemsOrden] = useState([]);

  const [buscar, setBuscar] = useState("");
  const [ordenarPor, setOrdenarPor] = useState("PRODUCTO");
  const [agruparPor, setAgruparPor] = useState("TIPO");

  const [pagina, setPagina] = useState(1);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const [dragKey, setDragKey] = useState("");

  const pageSize = 15;

  const cargarPedidos = async ({ signal } = {}) => {
    try {
      setLoading(true);
      setError("");

      const data = await getPedidosDisponiblesOrdenPedido({
        buscar,
        ordenarPor,
        signal,
      });

      setPedidos(Array.isArray(data?.pedidos) ? data.pedidos : []);
      setResumen(data?.resumen || {});
      setPagina(1);
    } catch (err) {
      if (err?.name === "CanceledError" || err?.name === "AbortError") return;

      console.error("Error cargando pedidos disponibles:", err);
      setError(
        err?.response?.data?.mensaje ||
          err?.response?.data?.error ||
          err?.message ||
          "Error al cargar pedidos disponibles"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const timer = setTimeout(() => {
      cargarPedidos({ signal: controller.signal });
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscar, ordenarPor]);

  const keysEnOrden = useMemo(
    () => new Set(itemsOrden.map((item) => item.pedidoKey)),
    [itemsOrden]
  );

const pedidosOrdenados = useMemo(() => {
  return ordenarLocal(pedidos, ordenarPor);
}, [pedidos, ordenarPor]);

const pedidosDisponiblesVista = useMemo(() => {
  return pedidosOrdenados.filter(
    (item) => !keysEnOrden.has(item.pedidoKey)
  );
}, [pedidosOrdenados, keysEnOrden]);

const totalPaginas = Math.max(
  1,
  Math.ceil(pedidosDisponiblesVista.length / pageSize)
);

const pedidosPagina = useMemo(() => {
  const desde = (pagina - 1) * pageSize;
  return pedidosDisponiblesVista.slice(desde, desde + pageSize);
}, [pedidosDisponiblesVista, pagina]);

  const cantidadSeleccionados = seleccionados.size;

  const resumenDerecha = useMemo(() => {
    const productos = new Set();
    const tipos = new Set();
    const provincias = new Set();

    let unidades = 0;

    for (const item of itemsOrden) {
      if (item.articulo) productos.add(normalizar(item.articulo));
      if (getTipoPedido(item)) tipos.add(normalizar(getTipoPedido(item)));
      if (item.provincia) provincias.add(normalizar(item.provincia));
      unidades += Number(item.cantidadOrdenada || item.cantidad || 0);
    }

    return {
      productos: productos.size,
      tipos: tipos.size,
      provincias: provincias.size,
      unidades,
    };
  }, [itemsOrden]);

  const toggleSeleccion = (pedidoKey) => {
    if (!pedidoKey || keysEnOrden.has(pedidoKey)) return;

    setSeleccionados((prev) => {
      const nuevo = new Set(prev);

      if (nuevo.has(pedidoKey)) {
        nuevo.delete(pedidoKey);
      } else {
        nuevo.add(pedidoKey);
      }

      return nuevo;
    });
  };

  const seleccionarTodosPagina = () => {
    setSeleccionados((prev) => {
      const nuevo = new Set(prev);

      for (const item of pedidosPagina) {
        if (!keysEnOrden.has(item.pedidoKey)) {
          nuevo.add(item.pedidoKey);
        }
      }

      return nuevo;
    });
  };

  const limpiarSeleccion = () => {
    setSeleccionados(new Set());
  };

  const agregarPedidosPorKeys = (keys = []) => {
    const setKeys = new Set(keys.map(String).filter(Boolean));

    if (!setKeys.size) return;

    setItemsOrden((prev) => {
      const yaEstan = new Set(prev.map((item) => item.pedidoKey));

      const nuevos = pedidos
        .filter((item) => setKeys.has(item.pedidoKey))
        .filter((item) => !yaEstan.has(item.pedidoKey))
        .map((item) => ({
          ...item,
          cantidadOrdenada: Number(item.cantidad || 1),
        }));

      return [...prev, ...nuevos];
    });

    setSeleccionados((prev) => {
      const nuevo = new Set(prev);
      for (const key of setKeys) nuevo.delete(key);
      return nuevo;
    });
  };

  const pasarSeleccionados = () => {
    agregarPedidosPorKeys([...seleccionados]);
  };

  const agregarUno = (item) => {
    if (!item?.pedidoKey) return;
    agregarPedidosPorKeys([item.pedidoKey]);
  };

  const quitarDeOrden = (pedidoKey) => {
    setItemsOrden((prev) => prev.filter((item) => item.pedidoKey !== pedidoKey));
  };

  const limpiarOrden = () => {
    if (!itemsOrden.length) return;
    const ok = window.confirm("¿Limpiar todos los pedidos seleccionados?");
    if (!ok) return;
    setItemsOrden([]);
  };

  const cambiarCantidad = (pedidoKey, value) => {
    const cantidad = Math.max(1, Number(value || 1));

    setItemsOrden((prev) =>
      prev.map((item) => {
        if (item.pedidoKey !== pedidoKey) return item;

        const max = Math.max(1, Number(item.cantidad || 1));

        return {
          ...item,
          cantidadOrdenada: Math.min(cantidad, max),
        };
      })
    );
  };

  const generarOrden = async () => {
    if (!itemsOrden.length) {
      alert("Debe seleccionar al menos un pedido.");
      return;
    }

    const ok = window.confirm(
      `¿Generar orden de pedido con ${itemsOrden.length} pedido/s?`
    );

    if (!ok) return;

    try {
      setGuardando(true);

      const orden = await generarOrdenPedido({
        ordenarPor,
        observacion: "ORDEN ARMADA DESDE ORGANIZAR PEDIDO",
        items: itemsOrden.map((item) => ({
          pedidoKey: item.pedidoKey,
          cantidadOrdenada: Number(item.cantidadOrdenada || item.cantidad || 1),
        })),
      });

      alert(
        `Orden de pedido generada correctamente${
          orden?.nroOrdenPedido ? ` Nº ${orden.nroOrdenPedido}` : ""
        }.`
      );

      setItemsOrden([]);
      setSeleccionados(new Set());
      await cargarPedidos();
    } catch (err) {
      console.error("Error generando orden:", err);

      alert(
        err?.response?.data?.mensaje ||
          err?.response?.data?.error ||
          err?.message ||
          "Error al generar la orden de pedido"
      );
    } finally {
      setGuardando(false);
    }
  };

  const onDragStart = (pedidoKey) => {
    setDragKey(pedidoKey);
  };

  const onDropDerecha = (e) => {
    e.preventDefault();

    if (!dragKey) return;

    if (seleccionados.has(dragKey)) {
      agregarPedidosPorKeys([...seleccionados]);
    } else {
      agregarPedidosPorKeys([dragKey]);
    }

    setDragKey("");
  };

  const renderPaginador = () => {
    if (totalPaginas <= 1) return null;

    const paginas = [];

    for (let i = 1; i <= totalPaginas; i++) {
      if (
        i === 1 ||
        i === totalPaginas ||
        Math.abs(i - pagina) <= 1
      ) {
        paginas.push(i);
      } else if (paginas[paginas.length - 1] !== "...") {
        paginas.push("...");
      }
    }

    return (
      <div className="op-pagination">
        {paginas.map((p, idx) =>
          p === "..." ? (
            <span key={`sep-${idx}`} className="op-page-sep">
              ...
            </span>
          ) : (
            <button
              key={p}
              type="button"
              className={`op-page ${pagina === p ? "active" : ""}`}
              onClick={() => setPagina(p)}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          className="op-page"
          disabled={pagina >= totalPaginas}
          onClick={() => setPagina((x) => Math.min(totalPaginas, x + 1))}
        >
          ›
        </button>
      </div>
    );
  };

  return (
    <div className="op-page-wrap">
      {/* <div className="op-topbar">
        <div className="op-brand">
          <div className="op-logo-mark">R</div>
          <div>
            <div className="op-brand-main">RIDERS</div>
            <div className="op-brand-sub">MIAMI</div>
          </div>
        </div>

        <div className="op-breadcrumb">
          <span>Pedidos</span>
          <span>›</span>
          <strong>Organizar Pedido</strong>
        </div>

        <div className="op-user">
          <div className="op-user-icon">●</div>
          <div>
            <div>Usuario</div>
            <small>Admin</small>
          </div>
        </div>
      </div> */}

      <div className="op-header">
        <div>
          <h1>Organizar pedido</h1>
          <p>Seleccione pedidos del listado y páselos a la orden.</p>
        </div>

        <div className="op-header-actions">
          <button
            type="button"
            className="op-btn op-btn-outline"
            onClick={() => {
              setItemsOrden([]);
              setSeleccionados(new Set());
            }}
          >
            Cancelar
          </button>

          <button
            type="button"
            className="op-btn op-btn-danger"
            disabled={guardando || !itemsOrden.length}
            onClick={generarOrden}
          >
            {guardando ? "Generando..." : "Generar orden de pedido"}
          </button>
        </div>
      </div>

      {error && <div className="op-alert op-alert-error">{error}</div>}

      <div className="op-grid">
        <section className="op-card op-left">
          <div className="op-card-title">
            <h2>Listado de pedidos disponibles</h2>
          </div>

          <div className="op-filters">
            <div className="op-search">
              <input
                type="text"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                placeholder="Buscar producto, receptor, teléfono, track..."
              />
              <span>🔍</span>
            </div>

            <label className="op-field">
              <span>Ordenar por</span>
              <select
                value={ordenarPor}
                onChange={(e) => setOrdenarPor(e.target.value)}
              >
                <option value="PRODUCTO">Producto</option>
                <option value="TIPO">Tipo</option>
                <option value="PROVINCIA">Provincia</option>
                <option value="MUNICIPIO">Municipio</option>
                <option value="ZONA">Zona logística</option>
                <option value="FECHA">Fecha</option>
                <option value="RECEPTOR">Receptor</option>
                <option value="TRACK">Track</option>
              </select>
            </label>

            <label className="op-field">
              <span>Agrupar por</span>
              <select
                value={agruparPor}
                onChange={(e) => setAgruparPor(e.target.value)}
              >
                <option value="TIPO">Tipo</option>
                <option value="PRODUCTO">Producto</option>
                <option value="PROVINCIA">Provincia</option>
                <option value="MUNICIPIO">Municipio</option>
                <option value="ZONA">Zona logística</option>
                <option value="">Sin agrupar</option>
              </select>
            </label>
          </div>

          <div className="op-selection-bar">
            <span>Seleccionar:</span>

            <button type="button" onClick={seleccionarTodosPagina}>
              Todos
            </button>

            <button type="button" onClick={limpiarSeleccion}>
              Ninguno
            </button>

            <strong>
                {loading
                ? "Cargando..."
                : `${pedidosDisponiblesVista.length} pedidos disponibles`}
            </strong>

            {cantidadSeleccionados > 0 && (
              <span className="op-selected-count">
                {cantidadSeleccionados} seleccionado/s
              </span>
            )}
          </div>

          <div className="op-table-wrap">
            <table className="op-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}></th>
                  <th>Producto</th>
                  <th>Código</th>
                  <th>Provincia</th>
                  <th>Tipo</th>
                  <th>Track</th>
                </tr>
              </thead>

              <tbody>
                {pedidosPagina.map((item, index) => {
                  const enOrden = keysEnOrden.has(item.pedidoKey);
                  const seleccionado = seleccionados.has(item.pedidoKey);
                  const grupo =
                    agruparPor && getGrupoValor(item, agruparPor);

                  const anterior = pedidosPagina[index - 1];
                  const grupoAnterior =
                    agruparPor && anterior
                      ? getGrupoValor(anterior, agruparPor)
                      : null;

                  const mostrarGrupo =
                    agruparPor && normalizar(grupo) !== normalizar(grupoAnterior);

                  return (
                    <React.Fragment key={item.pedidoKey}>
                      {mostrarGrupo && (
                        <tr className="op-group-row">
                          <td colSpan="6">{grupo}</td>
                        </tr>
                      )}

                      <tr
                        className={`${seleccionado ? "selected" : ""} ${
                          enOrden ? "in-order" : ""
                        }`}
                        draggable={!enOrden}
                        onDragStart={() => onDragStart(item.pedidoKey)}
                        onDoubleClick={() => agregarUno(item)}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={seleccionado || enOrden}
                            disabled={enOrden}
                            onChange={() => toggleSeleccion(item.pedidoKey)}
                          />
                        </td>
                        <td title={item.articulo}>{texto(item.articulo)}</td>
                        <td>{texto(item.codprod)}</td>
                        <td>{texto(item.provincia)}</td>
                        <td>{texto(getTipoPedido(item))}</td>
                        <td>{texto(item.track)}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}

                {!loading && pedidosPagina.length === 0 && (
                  <tr>
                    <td colSpan="6" className="op-empty">
                      No hay pedidos disponibles.
                    </td>
                  </tr>
                )}

                {loading && (
                  <tr>
                    <td colSpan="6" className="op-empty">
                      Cargando pedidos...
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {renderPaginador()}
        </section>

        <div className="op-transfer">
          <button
            type="button"
            className="op-transfer-btn"
            disabled={!seleccionados.size}
            onClick={pasarSeleccionados}
            title="Pasar seleccionados"
          >
            ›
          </button>

          <button
            type="button"
            className="op-transfer-btn"
            disabled={!itemsOrden.length}
            onClick={limpiarOrden}
            title="Limpiar pedido"
          >
            ‹
          </button>

          <div className="op-drag-help">
            ↕<br />
            O arrastre los pedidos seleccionados hacia la derecha
          </div>
        </div>

        <section
          className="op-card op-right"
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDropDerecha}
        >
          <div className="op-card-title op-right-title">
            <h2>Pedido a generar</h2>
            <span>{itemsOrden.length} pedido/s seleccionado/s</span>
          </div>

          <div className="op-table-wrap op-right-table-wrap">
            <table className="op-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Código</th>
                  <th>Provincia</th>
                  <th>Tipo</th>
                  <th>Fecha</th>
                  <th>Cantidad</th>
                  <th>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {itemsOrden.map((item) => {
                  const max = Math.max(1, Number(item.cantidad || 1));

                  return (
                    <tr key={item.pedidoKey}>
                      <td title={item.articulo}>{texto(item.articulo)}</td>
                      <td>{texto(item.codprod)}</td>
                      <td>{texto(item.provincia)}</td>
                      <td>{texto(getTipoPedido(item))}</td>
                      <td>{fechaCorta(item.fechaPedidoCliente)}</td>
                      <td>
                        <select
                          className="op-qty"
                          value={Number(item.cantidadOrdenada || 1)}
                          onChange={(e) =>
                            cambiarCantidad(item.pedidoKey, e.target.value)
                          }
                        >
                          {Array.from({ length: max }).map((_, idx) => (
                            <option key={idx + 1} value={idx + 1}>
                              {idx + 1}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="op-delete"
                          onClick={() => quitarDeOrden(item.pedidoKey)}
                          title="Quitar"
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {!itemsOrden.length && (
                  <tr>
                    <td colSpan="7" className="op-empty op-empty-big">
                      Arrastre pedidos aquí o seleccione registros de la izquierda y presione “›”.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="op-clear-wrap">
            <button
              type="button"
              className="op-clear"
              disabled={!itemsOrden.length}
              onClick={limpiarOrden}
            >
              Limpiar todo
            </button>
          </div>

          <div className="op-summary">
            <div>
            <strong>{itemsOrden.length}</strong>
            <span>Pedidos</span>
            </div>

            <div>
              <strong>{resumenDerecha.tipos}</strong>
              <span>Tipos</span>
            </div>

            <div>
              <strong>{resumenDerecha.provincias}</strong>
              <span>Provincias</span>
            </div>

            <div>
              <strong>{resumenDerecha.unidades}</strong>
              <span>Total unidades</span>
            </div>
          </div>

          <div className="op-alert op-alert-info">
            <strong>Información</strong>
            <span>
              Revise los pedidos seleccionados antes de generar la orden de pedido.
            </span>
          </div>
        </section>
      </div>

      <div className="op-help">
        <strong>💡 Cómo funciona:</strong>
        <span>
          1. Seleccione pedidos en el listado de la izquierda. 2. Haga clic en
          “›” o arrastre hacia la derecha. 3. Ajuste cantidades si corresponde.
          4. Genere la orden de pedido.
        </span>
      </div>

      <div className="op-bottom-resumen">
        <span>Disponibles: {pedidosDisponiblesVista.length}</span>
        <span>Unidades: {resumen?.unidades ?? 0}</span>
        <span>Sin track: {resumen?.sinTrack ?? 0}</span>
        <span>Sin teléfono: {resumen?.sinTelefono ?? 0}</span>
      </div>
    </div>
  );
}